import {Menu} from "lucide-react";
import {useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {Button} from "@/components/ui/button";
import {Sheet, SheetContent} from "@/components/ui/sheet";
import {cn} from "@/lib/utils";
import {hasActiveBoardSeatForProperty} from "@/src/modules/core/lib/board-seat";
import useResidentBoardNavRoutes from "@/src/modules/routing/hooks/use-resident-board-nav-routes";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";

import {useStoredMe} from "../store/me";
import {useStoredUserProperty} from "../store/user-property";

type ResidentBoardNavTriggerProps = {
  className?: string;
};

export function ResidentBoardNavTrigger({className}: ResidentBoardNavTriggerProps) {
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const routes = useResidentBoardNavRoutes();

  const isVisible =
    Boolean(me?.isResident) && hasActiveBoardSeatForProperty(me, userProperty?.id);

  if (!isVisible || routes.length === 0) {
    return null;
  }

  const isSheetRouteActive = routes.some(
    (route) =>
      location.pathname === route.path || location.pathname.startsWith(`${route.path}/`),
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn(
          "relative shrink-0",
          isSheetRouteActive && "bg-accent font-semibold text-foreground",
          className,
        )}
        aria-label="Board modules"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Menu
          className={cn(
            "size-4 shrink-0",
            isSheetRouteActive ? "text-foreground" : "text-primary",
          )}
          aria-hidden
        />
      </Button>

      <ResidentBoardNavSheet open={open} onOpenChange={setOpen} routes={routes} />
    </>
  );
}

type ResidentBoardNavSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  routes: ReturnType<typeof useResidentBoardNavRoutes>;
};

function ResidentBoardNavSheet({open, onOpenChange, routes}: ResidentBoardNavSheetProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="flex min-h-0 w-full max-w-[min(100vw,17.5rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[17.5rem] lg:max-w-[17.5rem]">
        <SheetHelpHeader title="Board modules" />
        <nav
          aria-label="Board modules"
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-4"
        >
          {routes.map((route) => {
            const Icon = route.icon;
            const isActive =
              location.pathname === route.path ||
              location.pathname.startsWith(`${route.path}/`);

            return (
              <Button
                key={route.path}
                type="button"
                variant="ghost"
                className={cn(
                  "h-auto min-h-10 w-full justify-start gap-2 px-2 py-2 text-sm font-normal",
                  isActive && "bg-blue-200 font-semibold !text-blue-800",
                )}
                onClick={() => {
                  onOpenChange(false);
                  navigate(route.path);
                }}
              >
                {Icon ? (
                  <span className="flex size-4 shrink-0 items-center justify-center text-blue-600">
                    <Icon className="size-4" />
                  </span>
                ) : null}
                <span className="truncate text-left">{route.label}</span>
              </Button>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
